import React, { useMemo } from 'react';
import styled from 'styled-components';
import { VotingRound } from '../../../domain/entities/Room';
import { TokenSet } from '../../styles/tokens';
import { getVotingMessage } from '../../../domain/valueObjects/VotingMessages';
import { useLanguage } from '../../contexts/LanguageContext';
import { getCardLabel } from '../../i18n/cardLabels';

const ResultsBox = styled.div<{ tokens: TokenSet }>`
  background: ${p => p.tokens.bgSurface};
  border: 1px solid ${p => p.tokens.border};
  border-radius: 16px;
  padding: 14px 18px;
  text-align: center;
`;

const Message = styled.div<{ tokens: TokenSet }>`
  font-size: 15px;
  font-weight: 700;
  color: ${p => p.tokens.text};
  margin-bottom: 10px;
`;

const StatsRow = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: 10px;
`;

const Stat = styled.div<{ tokens: TokenSet }>`
  text-align: center;
`;

const StatValue = styled.div<{ tokens: TokenSet; accent?: boolean }>`
  font-size: 22px;
  font-weight: 900;
  color: ${p => p.accent ? p.tokens.primary : p.tokens.text};
`;

const StatLabel = styled.div<{ tokens: TokenSet }>`
  font-size: 11px;
  color: ${p => p.tokens.textMuted};
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const VoteList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: center;
`;

const VoteChip = styled.div<{ tokens: TokenSet }>`
  background: ${p => p.tokens.bgElevated};
  border: 1px solid ${p => p.tokens.border};
  border-radius: 20px;
  padding: 3px 10px;
  font-size: 12px;
  color: ${p => p.tokens.textMuted};
`;

interface VotingResultsProps {
  round: VotingRound;
  participantNames: Record<string, string>;
  tokens: TokenSet;
}

export const VotingResults: React.FC<VotingResultsProps> = ({ round, participantNames, tokens }) => {
  const { t, language } = useLanguage();
  const deckType = round.deckType || 'fibonacci';
  const voteValues = Object.values(round.votes).filter(Boolean);
  const message = useMemo(
    () => getVotingMessage(voteValues, language, deckType),
    // Only re-pick a phrase when a new round is revealed or the language changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [round.completedAt, language]
  );

  const finalLabel = round.finalEstimate
    ? getCardLabel(round.finalEstimate, deckType, t)
    : '?';
  const finalLabelText = deckType === 'tshirt' ? t.mostCommonSize : t.finalEstimate;

  return (
    <ResultsBox tokens={tokens}>
      <Message tokens={tokens}>{message}</Message>

      <StatsRow>
        <Stat tokens={tokens}>
          <StatValue tokens={tokens} accent>{finalLabel}</StatValue>
          <StatLabel tokens={tokens}>{finalLabelText}</StatLabel>
        </Stat>
        {deckType === 'fibonacci' && round.average !== null && (
          <Stat tokens={tokens}>
            <StatValue tokens={tokens}>{round.average}</StatValue>
            <StatLabel tokens={tokens}>{t.average}</StatLabel>
          </Stat>
        )}
        <Stat tokens={tokens}>
          <StatValue tokens={tokens}>{voteValues.length}</StatValue>
          <StatLabel tokens={tokens}>{t.votesLabel}</StatLabel>
        </Stat>
      </StatsRow>

      <VoteList>
        {Object.entries(round.votes).map(([uid, vote]) => (
          <VoteChip key={uid} tokens={tokens}>
            {participantNames[uid] || uid}: <strong>{getCardLabel(vote, deckType, t)}</strong>
          </VoteChip>
        ))}
      </VoteList>
    </ResultsBox>
  );
};
